import { detectRTLOffsetType } from '../../../utils';

/**
 * Returns a normalized scrollLeft value for a container element, abstracting away browser RTL quirks.
 *
 * In most browsers and LTR mode, scrollLeft reports the logical left position.
 * For RTL containers, browsers handle scrollLeft inconsistently:
 *   - "negative": scrollLeft starts at 0 (right edge) and becomes negative as content scrolls left (e.g., Firefox).
 *   - "reverse": scrollLeft starts at the max value (left edge) and decreases towards 0 (right edge) (e.g., Edge, IE).
 *   - "default": scrollLeft behaves as in LTR (e.g., Chrome, Safari).
 *
 * This function detects the browser RTL offset type and returns a normalized value such that 0 means fully scrolled to the right
 * (the visual start for RTL), and higher values move the viewport leftward.
 *
 * @param container - The scroll container element.
 * @param isRTL - True if the container is rendered in RTL mode.
 * @returns The normalized scrollLeft value (0 = rightmost; increases leftward in RTL).
 */
export function getNormalizedScrollLeft(container: HTMLElement, isRTL: boolean): number {
    if (!isRTL) return container.scrollLeft;

    const type = detectRTLOffsetType();
    switch (type) {
        case 'negative':
            return container.scrollWidth - container.clientWidth + container.scrollLeft;
        case 'reverse':
            return container.scrollWidth - container.clientWidth - container.scrollLeft;
        default:
            return container.scrollLeft;
    }
}
